import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphql } from '@/lib/graphql';
import { apolloClient } from '@/lib/graphql/client';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import type { SupportedLanguage } from './config';

const GET_TRANSLATION_DICTIONARY_QUERY = graphql(`
  query TranslationDictionary($domain: TranslationDomain!) {
    translationDictionary(domain: $domain) {
      lookupKey
      localizedTexts {
        ja
        en
      }
    }
  }
`);

type Dictionary = Map<string, string>;

const buildDictionary = (
  language: SupportedLanguage,
  data:
    | {
        lookupKey: string;
        localizedTexts: Record<SupportedLanguage, string | null>;
      }[]
    | undefined
): Dictionary => {
  const entries = data ?? [];
  const dictionary = new Map<string, string>();

  for (const item of entries) {
    const localized =
      item.localizedTexts?.[language] ?? item.localizedTexts?.en;
    dictionary.set(item.lookupKey, localized ?? item.lookupKey);
  }

  return dictionary;
};

export const useTranslationDictionary = (
  domain: 'topic_tag' | 'theme',
  options?: { enabled?: boolean }
) => {
  const language = getCurrentLanguage();

  const { data, isLoading, error } = useQuery({
    queryKey: ['translationDictionary', domain],
    queryFn: async () => {
      const result = await apolloClient.query({
        query: GET_TRANSLATION_DICTIONARY_QUERY,
        variables: { domain },
      });
      return result.data.translationDictionary;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: options?.enabled ?? true,
  });

  const dictionary = useMemo(
    () => buildDictionary(language, data),
    [language, data]
  );

  const getLabel = useCallback(
    (lookupKey: string) => dictionary.get(lookupKey) ?? lookupKey,
    [dictionary]
  );

  return {
    getLabel,
    isLoading,
    error,
  };
};
